import { clampOrbitLabel } from './contextLabel';
import { OrbitTurn } from './types';

export interface OrbitConversation {
  sessionId: string;
  /** Question of the oldest turn seen for the session; the label the user recognises it by. */
  firstQuestion: string;
  /** `created_at` of the newest turn seen for the session. */
  lastActiveAt: string;
  /** Turns seen for the session — only those loaded so far when the list is paged. */
  messageCount: number;
  /** `context.screen` of the oldest turn seen, the screen the conversation started on. */
  screen: string | undefined;
}

const byCreatedAtAsc = (first: OrbitTurn, second: OrbitTurn): number =>
  first.created_at.localeCompare(second.created_at) || first.id - second.id;

// Groups a flat turn list into chats, newest activity first.
export const groupTurnsBySession = (turns: OrbitTurn[]): OrbitConversation[] => {
  const bySession = new Map<string, OrbitTurn[]>();

  turns.forEach(turn => {
    const group = bySession.get(turn.session_id);

    if (group === undefined) {
      bySession.set(turn.session_id, [turn]);
    } else {
      group.push(turn);
    }
  });

  return Array.from(bySession.entries())
    .map(([sessionId, group]) => {
      const ordered = [...group].sort(byCreatedAtAsc);
      const oldest = ordered[0];
      const newest = ordered[ordered.length - 1];

      return {
        sessionId,
        firstQuestion: oldest.question,
        lastActiveAt: newest.created_at,
        messageCount: ordered.length,
        screen: oldest.context.screen
      };
    })
    .sort(
      (first, second) =>
        second.lastActiveAt.localeCompare(first.lastActiveAt) ||
        first.sessionId.localeCompare(second.sessionId)
    );
};

/**
 * The open chat's title before the server has a row for it: its first question, cut as the server
 * cuts a default title. A chat with no question on record has none, and each surface supplies its
 * own fallback.
 */
export const openChatTitle = (turns: OrbitTurn[]): string | undefined => {
  const firstQuestion = groupTurnsBySession(turns)[0]?.firstQuestion;

  return firstQuestion === undefined ? undefined : clampOrbitLabel(firstQuestion);
};

export default groupTurnsBySession;
